import { flattenFieldErrors, type ApiError } from '@/lib/problem-details'

/**
 * A refusal from the ledger, and what clears it.
 *
 * `resolveErrorMessage` in `src/lib/error-messages.ts` already turns a dotted code into a sentence
 * an operator can read. This adds the second half of the answer — the step that removes the
 * obstruction — for the codes the ledger raises. The two are kept apart on purpose: the message
 * says what happened and belongs in the alert; the remedy says what to do and belongs underneath
 * it, where somebody who already knows what happened is looking.
 *
 * Only codes this codebase already recognises appear here. Nothing is invented: a code the server
 * raises that is missing from this table falls through to the server's own detail text, which is
 * always shown, so an unmapped refusal is still readable rather than blank.
 */
const REMEDIES: Record<string, string> = {
  'ledger.day_close_blocked':
    'Clear what the day is waiting on, then close again. The usual holds are an unresolved balance drift alarm, a till that has not been reconciled, and work still sitting in the approvals queue — each of them is listed above when the server names it.',
  'ledger.business_day_closed':
    'This day is already closed. Its figures have been carried forward and nothing in this application reopens one.',
  'ledger.business_day_not_opened':
    'The branch has no open day for that date. Open it on the business-days screen first — postings and a close both need one.',
  'ledger.business_day_already_closed':
    'The day has already been closed, by somebody else or by an earlier attempt that succeeded. Re-read the register before doing anything further.',
  'ledger.business_day_in_future':
    'A day cannot be opened or closed ahead of the server’s own clock. Check the date.',
  'ledger.period_closed':
    'The accounting period covering that date is closed. A soft close can be lifted by reopening the period, which is countersigned; a sealed period needs the same and should be a considered decision.',
  'ledger.unbalanced_journal':
    'Debits and credits must be equal to the pesewa. The manual journal form will not submit an unbalanced entry, so this refusal means the totals changed between the form and the server — reload and re-key it.',
  'ledger.account_not_postable':
    'That account is a heading. Only the postable accounts at the bottom of a branch of the chart take entries — the chart screen marks which ones those are.',
  'access.permission_denied':
    'Your role does not carry this operation. The ledger permissions are granted by an administrator, and the server checks each one on every request.',
  'access.branch_not_permitted':
    'You cannot act on that branch. The branches you may operate in are granted by an administrator and are the only ones offered in the pickers here.',
  'access.maker_is_checker':
    'You submitted this request yourself, so you cannot also countersign it. Somebody else holding the approving permission has to.',
  'access.approval_expired':
    'The approval request lapsed before anybody signed it. Ledger journal requests expire twelve hours after they are made — half the institution’s usual window. Submit it again.',
  'common.concurrency_conflict':
    'Somebody changed this while you were looking at it. Reload before acting, so you are acting on what is actually there.',
}

/** The step that clears this refusal, or nothing when the code carries no specific remedy. */
export const ledgerErrorRemedy = (code: string): string | null => REMEDIES[code] ?? null

/**
 * One thing standing in the way, as the screen renders it.
 *
 * `source` says where the client learned of it, and it matters. `server` means the API named this
 * in its refusal and it is authoritative. `client` means this screen worked it out from a read it
 * made itself — useful before the click, and never a claim that the server would agree.
 */
export interface LedgerObstruction {
  id: string
  /** What is in the way, in a few words. */
  title: string
  /** What clears it. Every obstruction has one, or it should not be shown as an obstruction. */
  remedy: string
  source: 'server' | 'client'
}

/**
 * Everything a refusal names, one obstruction per line.
 *
 * A day close is refused for a *set* of reasons, and the set is the useful output — a screen that
 * showed the first and dropped the rest would send a branch manager round the loop once per
 * obstruction, at the end of a working day, each time discovering one more. So the problem
 * document is read for all of it:
 *
 * - the validation members (`errors` in RFC 9457 / ASP.NET's problem shape), which is where a
 *   handler that refuses for several reasons at once puts them, one array entry per reason;
 * - the dotted code, whose remedy is added as its own line when the field members did not already
 *   carry one.
 *
 * What this cannot do is enumerate obstructions the server did not name. The contract publishes no
 * structured obstruction list on the close endpoint — only the problem document — so this reads
 * that document thoroughly and states the remedy for what it finds. The pre-flight checks the close
 * screen runs beside this are the client's own reads, marked as such.
 */
export function obstructionsFromError(error: ApiError): LedgerObstruction[] {
  const remedy = ledgerErrorRemedy(error.code)

  const fromFields = flattenFieldErrors(error.fieldErrors).map(
    (message, index): LedgerObstruction => ({
      id: `field-${String(index)}`,
      title: message,
      remedy:
        remedy ??
        'The server named this specifically. Clear it and try the close again — nothing has been closed.',
      source: 'server',
    }),
  )

  if (fromFields.length > 0) {
    return fromFields
  }

  // No field members: the code and its detail are the whole of what the server said, so that is
  // what gets shown. `detail` is never dropped — an unmapped code with a detail line is still the
  // most specific thing anybody has.
  const title = error.detail ?? error.title
  return [
    {
      id: error.code || 'refusal',
      title,
      remedy: remedy ?? 'The server gave no further detail. Quote the reference below to support.',
      source: 'server',
    },
  ]
}
